import * as Sentry from "@sentry/react";
import { createContext, useCallback, useEffect, useState } from "react";

import sfAuth from "~/auth/SFAuth";
import useInterval from "~/auth/useInterval";
import Config from "~/config";
import logger from "~/helpers/logger";

const defaultAuthContext: AuthContextValue = {
	currentUser: null,
	error: null,
	isCustomer: false,
	loggedIn: false,
	performCodeExchange: () => Promise.resolve(),
	redirectToLogin: () => {},
	redirectToLogout: () => {},
	sessionChecked: false,
	wsapiTenant: "",
	wsUserType: ""
};

// biome-ignore lint/style/useComponentExportOnlyModules: AuthContext must be exported for consumers (RequireAuth, OAuthRedirect)
export const AuthContext = createContext<AuthContextValue>(defaultAuthContext);

interface AuthProviderProps {
	children: React.ReactNode;
}

const TOKEN_DURATION_SECONDS = Config.tokenDuration;

/** Paths that may render without an authenticated session — never redirect away from these. */
const PUBLIC_UNAUTHENTICATED_PATH_PREFIXES = ["/no-redirect", "/forbidden"];
const isPublicUnauthenticatedPath = (): boolean =>
	PUBLIC_UNAUTHENTICATED_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));

/** Decode a JWT payload (id_token) into a claims bag without verifying the signature. */
const decodeJwtPayload = (token: string): Record<string, unknown> => {
	const base64Url = token.split(".")[1];
	const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
	const json = decodeURIComponent(
		window
			.atob(base64)
			.split("")
			.map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
			.join("")
	);
	return JSON.parse(json) as Record<string, unknown>;
};

const claimString = (claims: Record<string, unknown>, key: string): string | undefined => {
	const value = claims[key];
	return typeof value === "string" ? value : undefined;
};

/**
 * Derive display name + email from OIDC id_token claims.
 *   Display name: name → given_name + family_name → preferred_username → email
 *   Email: id_token.email → access-token email → ""
 */
const deriveDisplayInfo = (
	idToken: string | undefined,
	accessTokenEmail: string | undefined
): { displayName: string; email: string; firstName: string; lastName: string } => {
	let firstName = "";
	let lastName = "";
	let email = accessTokenEmail || "";
	let displayName = email;

	if (idToken) {
		try {
			const claims = decodeJwtPayload(idToken);
			const claimEmail = claimString(claims, "email");
			if (claimEmail) {
				email = claimEmail;
			}
			displayName = email;

			const name = claimString(claims, "name");
			const givenName = claimString(claims, "given_name");
			const familyName = claimString(claims, "family_name");
			const preferredUsername = claimString(claims, "preferred_username");

			if (name) {
				displayName = name;
				const parts = name.split(" ");
				firstName = parts[0] || "";
				lastName = parts.slice(1).join(" ");
			} else if (givenName || familyName) {
				firstName = givenName || "";
				lastName = familyName || "";
				displayName = `${firstName} ${lastName}`.trim();
			} else if (preferredUsername) {
				displayName = preferredUsername;
			}
		} catch {
			logger.warn("Failed to decode id_token for display info");
		}
	}

	return { displayName, email, firstName, lastName };
};

function AuthProvider({ children }: AuthProviderProps) {
	const [loggedIn, setLoggedIn] = useState(false);
	const [sessionChecked, setSessionChecked] = useState(false);
	const [currentUser, setCurrentUser] = useState<Maybe<SFUser>>(null);
	const [authError, setAuthError] = useState<Maybe<string>>(null);
	const [wsapiTenant, setWsapiTenant] = useState("");
	const [wsUserType, setWsUserType] = useState("");
	const [isCustomer, setIsCustomer] = useState(false);

	const setUserFromTokens = useCallback(() => {
		const tokens = sfAuth.getTokens();
		if (!tokens) {
			setCurrentUser(null);
			return;
		}

		const decoded = sfAuth.parseTokenPayload(tokens.accessToken);
		const userType = decoded.wsUserType ?? "";
		setWsapiTenant(decoded.wsTenantId);
		setWsUserType(userType);
		setIsCustomer(userType === "customer" && !!decoded.wsCustomerId);

		const { displayName, email, firstName, lastName } = deriveDisplayInfo(tokens.idToken, decoded.email);

		setCurrentUser({
			displayName,
			email,
			firstName,
			lastName,
			roles: decoded.roles || [],
			uuid: decoded.sub,
			wsapiTenant: decoded.wsTenantId
		});
	}, []);

	/** Initial session check on mount; re-check when the browser comes back online. */
	useEffect(() => {
		(async () => {
			try {
				const currentToken = await sfAuth.checkSession();
				if (currentToken === "") {
					if (isPublicUnauthenticatedPath()) {
						return;
					}
					sfAuth.redirectToLogin();
				} else {
					setUserFromTokens();
					setLoggedIn(true);
				}
			} catch {
				setLoggedIn(false);
			} finally {
				setSessionChecked(true);
			}
		})();

		const handleOnline = () => {
			void sfAuth.checkSession();
		};
		window.addEventListener("online", handleOnline);
		return () => {
			window.removeEventListener("online", handleOnline);
		};
	}, [setUserFromTokens]);

	/** Periodic renewal — a 3600s token re-checks every ~6 minutes, well inside the 20% threshold. */
	useInterval(
		() => {
			(async () => {
				try {
					const currentToken = await sfAuth.checkSession();
					if (currentToken === "") {
						if (!isPublicUnauthenticatedPath()) {
							await sfAuth.redirectToLogin();
						}
					} else {
						try {
							setUserFromTokens();
						} catch (e: unknown) {
							logger.warn("Error parsing tokens:", e);
							Sentry.addBreadcrumb({ category: "auth", level: "error", message: `Error parsing tokens: ${e}` });
						}
					}
				} catch (e) {
					logger.warn("Error checking session:", e);
					Sentry.addBreadcrumb({ category: "auth", level: "error", message: `Error checking session: ${e}` });
					if (!isPublicUnauthenticatedPath()) {
						await sfAuth.redirectToLogin();
					}
				}
			})();
		},
		(TOKEN_DURATION_SECONDS * 1000) / 10
	);

	/** Perform the OAuth2 authorization-code exchange for `code` and hydrate the user. */
	const performCodeExchange = useCallback(
		async (code: string): Promise<void> => {
			try {
				await sfAuth.exchangePKCECodeForTokens(code);
				setUserFromTokens();
				setLoggedIn(true);
			} catch (e) {
				const message = e instanceof Error ? e.message : String(e);
				Sentry.addBreadcrumb({
					category: "auth",
					level: "error",
					message: `Error performing code exchange: ${message}`
				});
				setAuthError(message);
				throw e;
			}
		},
		[setUserFromTokens]
	);

	const contextValue: AuthContextValue = {
		currentUser,
		error: authError,
		isCustomer,
		loggedIn,
		performCodeExchange,
		// Bind so callers can destructure these from context without losing `this`.
		redirectToLogin: sfAuth.redirectToLogin.bind(sfAuth),
		redirectToLogout: sfAuth.redirectToLogout.bind(sfAuth),
		sessionChecked,
		wsapiTenant,
		wsUserType
	};

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
